import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import Router, { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { useTrackDepletedActionBlocked, useTrackDepletedUpsellClicked } from '@air/analytics';
import { type ClipsListResponse } from '@air/api';
import { type Clip } from '@air/api/types';
import { FileStatus } from '@air/feature-file-status/constants';
import {
  FileStatusIcon,
  FileStatusTrackingPanel,
  type FileTrackingItem,
  type FileTrackingItemCounts,
} from '@air/feature-file-status/ui';
import { Banner } from '@air/primitive-banner';
import { Button } from '@air/primitive-button';
import { useAirModal } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';

import { GalleryItemType } from '~/components/Gallery/types';
import { ImportCancellationModal } from '~/components/Import/ImportCancellationModal';
import {
  convertToFileTrackingItem,
  getImportHeaderTitles,
  type ImporterTaskEnhanced,
  normalizeLocalImport,
  normalizeRemoteImport,
} from '~/components/Import/utils';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { GALLERY_ASSETS } from '~/constants/react-query-keys';
import { isSubscriptionCheckoutRoute, Routes } from '~/constants/routes';
import { THREE_SECONDS } from '~/constants/unitsOfTime';
import { useCleanupImportPlaceholders } from '~/hooks/useCleanupImportPlaceholders';
import { useCreditAnalyticsParams } from '~/hooks/useCreditAnalyticsParams';
import { useGoToBoardPage } from '~/hooks/useGoToBoardPage';
import { useStoragePlanRestriction } from '~/hooks/useStoragePlanRestriction';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { type LocationBoard } from '~/store/router/types';
import { removeTasksAction } from '~/store/tasks/actions';
import { makeImporterTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { getAdImportDescriptionMetadata } from '~/store/tasks/utils';
import { useInvalidateAdMetricSources } from '~/swr-hooks/ads/meta/useInvalidateAdMetricSources';
import { getImportsKey, useImportTasks } from '~/swr-hooks/importer/useImportTasks';
import { useResumeImportMutation } from '~/swr-hooks/importer/useResumeImportMutation';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { ReadyState } from '~/types/sockets';
import { getPlaceholdersForTask } from '~/utils/importPlaceholderTracking';
import { useAddAssetsToAllViews, useRefreshAssetsInAllViews } from '~/utils/mutateUtils/GalleryAssets';
import { useRefreshBoardsInAllViews } from '~/utils/mutateUtils/GalleryBoards';
import { useRefreshSideNavBoards } from '~/utils/mutateUtils/SideNavBoards';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';
import { useAirSelector } from '~/utils/ReduxUtils';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';

export const ImportManager = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const currentBoardId = useURLBoardIdSelector();
  const { invalidateAdMetricSources } = useInvalidateAdMetricSources();
  const { cleanupPlaceholders } = useCleanupImportPlaceholders();
  const { refreshBoardsInAllViews } = useRefreshBoardsInAllViews();
  const { addAssetsToAllViews } = useAddAssetsToAllViews();
  const { refreshAssetsInAllViews } = useRefreshAssetsInAllViews();
  const queryClient = useQueryClient();
  const { refreshSideNavSubBoards, refreshRootSideNavBoards } = useRefreshSideNavBoards();
  const tasksSelector = useMemo(
    () => makeImporterTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );
  const dispatch = useDispatch();
  const importerTasks = useAirSelector(tasksSelector);
  const { showToast } = useToasts();

  const [isImportPaneVisible, setImportPaneVisibility] = useState(false);
  const { goToBoardPage } = useGoToBoardPage();
  const { hasImportsInProgress, imports, stoppedImports, hasStoppedImports } = useImportTasks({
    refetchInterval: 0,
  });

  const { mutation: resumeImportMutation } = useResumeImportMutation();

  const [showImportCancellationModal] = useAirModal(ImportCancellationModal);

  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canUpgradePlan = canManageSubscription(permissions);

  const [showPlansModal] = useAirModal(PlansModal);
  const { data: storage } = useStorage();
  const { data: subscription } = useSubscriptionContext();
  const { isStoragePlanRestricted } = useStoragePlanRestriction();
  const router = useRouter();
  const { trackDepletedActionBlocked } = useTrackDepletedActionBlocked();
  const { trackDepletedUpsellClicked } = useTrackDepletedUpsellClicked();
  const creditAnalyticsParams = useCreditAnalyticsParams();
  const hasTrackedImportBlocked = useRef(false);

  const closePane = useCallback(() => {
    setImportPaneVisibility(false);
    // Cleanup completed local importer tasks when the pane is closed.
    // This is to avoid showing incorrect status based on past completed tasks.
    dispatch(
      removeTasksAction({
        taskIds: importerTasks
          .filter(({ status }) => ['completed', 'error'].includes(status))
          .map(({ localTaskId }) => localTaskId),
      }),
    );
  }, [dispatch, importerTasks]);

  const onRowClick = useCallback(
    (board: LocationBoard) => {
      if (board.id) {
        goToBoardPage({
          board,
          trackLocation: 'import-popover',
        });
      } else {
        Router.push(Routes.media.all);
      }
    },
    [goToBoardPage],
  );

  const onCancelImport = useCallback(
    (item: FileTrackingItem) => {
      showImportCancellationModal({
        importId: item.id,
      });
    },
    [showImportCancellationModal],
  );

  const onResumeImport = useCallback(
    async (item: FileTrackingItem) => {
      const localTask = importerTasks.find((task) => task.localTaskId === item.id);

      if (localTask?.remoteTaskId) {
        await resumeImportMutation.mutateAsync(localTask.remoteTaskId);
      } else {
        showToast('Import not found.', { color: 'red' });
      }
    },
    [resumeImportMutation, importerTasks, showToast],
  );

  const handleResumeAll = useCallback(async () => {
    await Promise.all(stoppedImports.map(({ id }) => resumeImportMutation.mutateAsync(id)));
  }, [stoppedImports, resumeImportMutation]);

  const {
    loadFromStorage,
    syncLocalTasks,
    subscriptionsReady: taskSubscriptionsReady,
  } = usePrivateSyncTasks({
    workspaceId: currentWorkspace?.id,
    tasksSelector,
    localType: 'Importer',
    remoteType: 'Importer',
    onComplete: async ({ localTask }) => {
      const parentBoardId = localTask.metadata.parentBoardId;

      if (currentBoardId && parentBoardId === currentBoardId) {
        refreshBoardsInAllViews(currentBoardId);
      }

      if (!currentBoardId || currentBoardId === parentBoardId) {
        await refreshAssetsInAllViews({ parentBoardId });

        const tracked = getPlaceholdersForTask(localTask.localTaskId);

        if (tracked) {
          // After the refetch, check whether the placeholder clips are still in the
          // gallery cache. The refetch replaces the locally-mutated cache with fresh
          // backend data, which removes our placeholder clips. If the real clips have
          // not been indexed yet either, re-inject the placeholders so there is no
          // empty-slot gap while waiting for the delayed retry.
          const allCachedData = queryClient.getQueriesData<InfiniteData<ClipsListResponse<Clip>>>({
            queryKey: [GALLERY_ASSETS],
          });
          const allCachedClips = allCachedData.flatMap(
            ([, data]) => data?.pages.flatMap((page) => page.data.clips) ?? [],
          );
          const placeholderIdSet = new Set(tracked.clipIds);
          const anyPlaceholderInCache = allCachedClips.some((c) => placeholderIdSet.has(c.id));

          if (!anyPlaceholderInCache) {
            // Placeholders were replaced by the refetch. Check whether the real clips
            // arrived. The backend strips the file extension from importedName, so we
            // compare after stripping.
            const strippedImportedNames = new Set(
              tracked.clips.map((c) => c.importedName?.replace(/\.[^.]+$/, '')).filter((n): n is string => !!n),
            );
            const anyRealClipInCache = allCachedClips.some(
              (c) => c.importedName && strippedImportedNames.has(c.importedName),
            );

            if (!anyRealClipInCache) {
              addAssetsToAllViews({
                assets: tracked.clips,
                parentBoardId: tracked.parentBoardId,
                type: GalleryItemType.asset,
              });
            }
          }

          // The backend may not have indexed all clips by the time the first
          // refetch completes. A short delayed retry catches stragglers.
          delayedRefreshTimerRef.current = setTimeout(async () => {
            await refreshAssetsInAllViews({ parentBoardId });
            cleanupPlaceholders(localTask.localTaskId);
          }, THREE_SECONDS);
        }
      }

      if (currentWorkspace && !parentBoardId) {
        refreshRootSideNavBoards(currentWorkspace?.id);
      } else if (parentBoardId) {
        refreshSideNavSubBoards(parentBoardId);
      }

      if (localTask.metadata.source === 'meta') {
        const metadata = getAdImportDescriptionMetadata(localTask.metadata);

        if (metadata) {
          invalidateAdMetricSources(metadata.id);
        }
      }

      queryClient.setQueriesData<{ data: ImporterTaskEnhanced[] }>(
        {
          queryKey: getImportsKey({ workspaceId: currentWorkspace?.id }),
        },
        (old) =>
          old
            ? produce(old, (draft) => {
                const oldTask = draft.data.find((item) => item.id === localTask.remoteTaskId);

                if (oldTask) {
                  oldTask.status = 'succeeded';
                }
              })
            : undefined,
      );
    },
    onUpdate: async ({ task, enrichMetadata }) => {
      if (task.data) {
        enrichMetadata(task.data);
      }
    },
    onError: ({ localTask }) => {
      cleanupPlaceholders(localTask.localTaskId);
    },
  });

  const syncExecutedForReadyStateRef = useRef(false);
  const delayedRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * On mount, take the tasks from local storage and add them to Redux
   */
  useEffect(() => {
    if (currentWorkspace?.id) {
      loadFromStorage();
    }
  }, [loadFromStorage, currentWorkspace?.id]);

  const syncLocalTasksOnce = useCallback(() => {
    if (!syncExecutedForReadyStateRef.current) {
      syncLocalTasks();
      syncExecutedForReadyStateRef.current = true;
    }
  }, [syncLocalTasks]);

  /**
   * Reset sync flag when task subscriptions become not ready
   */
  useEffect(() => {
    if (!taskSubscriptionsReady) {
      syncExecutedForReadyStateRef.current = false;
    }
  }, [taskSubscriptionsReady]);

  /**
   * Sync tasks when WebSocket reconnects to catch any missed completion events.
   * Only sync when task subscriptions are confirmed ready to avoid race conditions.
   */
  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN && taskSubscriptionsReady) {
        syncLocalTasksOnce();
      }
    },
  });

  /**
   * Also sync when task subscriptions become ready after reconnection.
   */
  useEffect(() => {
    if (taskSubscriptionsReady) {
      syncLocalTasksOnce();
    }
  }, [taskSubscriptionsReady, syncLocalTasksOnce]);

  /**
   * When this panel unmounts (user logs out most likely),
   * reset the state
   */
  useUnmount(() => {
    if (delayedRefreshTimerRef.current) {
      clearTimeout(delayedRefreshTimerRef.current);
    }
    dispatch(
      removeTasksAction({
        taskIds: importerTasks.map(({ localTaskId }) => localTaskId),
      }),
    );
  });

  const inProgressImportItems =
    importerTasks.map((item) =>
      convertToFileTrackingItem({
        item: normalizeLocalImport(item),
        onRowClick,
        subscription,
        isOutOfStorage: isStoragePlanRestricted,
        includeErrorInSummary: true,
      }),
    ) || [];
  const completedImportItems =
    imports
      .filter(({ id }) => !importerTasks.some((task) => task.remoteTaskId === id))
      .map((item) =>
        convertToFileTrackingItem({
          item: normalizeRemoteImport(item),
          onRowClick,
          subscription,
          isOutOfStorage: isStoragePlanRestricted,
          includeErrorInSummary: false,
        }),
      ) || [];
  const importItems = [...inProgressImportItems, ...completedImportItems];

  // Auto-open the import pane when a new import starts. We track both remote
  // imports (hasImportsInProgress) and local Redux tasks (importerTasks.length)
  // because local tasks update synchronously on createTask dispatch, while
  // remote data may lag behind. closePane removes completed/errored tasks from
  // Redux, so after dismiss a new import will bump the count and re-trigger.
  // biome-ignore lint/correctness/useExhaustiveDependencies(isImportPaneVisible):  we want to call this only when importsInProgress or local task count changes
  useEffect(() => {
    if ((hasImportsInProgress || importerTasks.length > 0) && !isImportPaneVisible) {
      setImportPaneVisibility(true);
    }
  }, [hasImportsInProgress, importerTasks.length]);

  const handleImportBannerUpgradeClick = useCallback(() => {
    if (creditAnalyticsParams) {
      trackDepletedUpsellClicked({
        surface: 'banner',
        days_since_cycle_start: creditAnalyticsParams.days_since_cycle_start,
      });
    }
    showPlansModal();
  }, [creditAnalyticsParams, showPlansModal, trackDepletedUpsellClicked]);

  const handleStorageLimitUpgradeClick = useCallback(() => {
    showPlansModal();
  }, [showPlansModal]);

  // Guard prevents duplicate events; ref resets when imports resume so future blocks are tracked
  useEffect(() => {
    if (hasStoppedImports && subscription?.isCreditModel && creditAnalyticsParams && !hasTrackedImportBlocked.current) {
      hasTrackedImportBlocked.current = true;
      trackDepletedActionBlocked({
        action: 'import',
        days_since_cycle_start: creditAnalyticsParams.days_since_cycle_start,
      });
    } else if (!hasStoppedImports) {
      hasTrackedImportBlocked.current = false;
    }
  }, [hasStoppedImports, subscription?.isCreditModel, creditAnalyticsParams, trackDepletedActionBlocked]);

  const getImportSummary = useCallback(
    (params: { erroredCount: number; progressingCount: number; pausedCount: number; stoppedCount: number }) => {
      const isUpgradingPlan = isSubscriptionCheckoutRoute(router.pathname);

      if (isUpgradingPlan) {
        return null;
      }

      return params.stoppedCount && (subscription?.plan === 'basic' || isStoragePlanRestricted) ? (
        <Banner
          className="text-12"
          appearance="warning"
          suffix={
            canUpgradePlan && (
              <Button
                className="mt-1 font-medium text-inherit underline hover:underline"
                appearance="ghost"
                size="small"
                onClick={subscription?.isCreditModel ? handleImportBannerUpgradeClick : handleStorageLimitUpgradeClick}
              >
                Upgrade
              </Button>
            )
          }
        >
          {subscription?.isCreditModel
            ? "You're out of credits, imports stopped until your credits refresh or you upgrade your plan."
            : `You've hit your ${storage?.maxGb}GB import limit for free plans. Upgrade to continue.`}
        </Banner>
      ) : null;
    },
    [
      canUpgradePlan,
      handleImportBannerUpgradeClick,
      handleStorageLimitUpgradeClick,
      storage?.maxGb,
      subscription?.plan,
      router.pathname,
      isStoragePlanRestricted,
      subscription?.isCreditModel,
    ],
  );

  const getImportHeaderTitle = useCallback(
    (counts: FileTrackingItemCounts, status: FileStatus) => {
      const titles = getImportHeaderTitles(counts);
      const isInFreePlan = subscription?.isInFreePlan;
      const isUpgradingPlan = isSubscriptionCheckoutRoute(router.pathname);
      const isUpgradeSuccess = isUpgradingPlan && subscription?.plan === router.query.planId;

      // If the user is in a free plan and is upgrading, we want to show the upgrade in progress status
      if (isInFreePlan && isUpgradingPlan && counts.stoppedCount > 0) {
        return {
          title: 'Upgrade in progress',
          subtitle: 'Your import will restart after upgrade',
        };
      }

      // If the upgrade is successful, we want to show the success status
      if (!isInFreePlan && isUpgradeSuccess && counts.stoppedCount > 0) {
        return {
          title: 'Upgrade successful',
          subtitle: 'Import ready to restart',
        };
      }

      return titles[status];
    },
    [subscription?.isInFreePlan, router.pathname, router.query.planId, subscription?.plan],
  );

  const getImportSummaryTrackingAvatar = useCallback(
    (counts: FileTrackingItemCounts, status: FileStatus) => {
      const isInFreePlan = subscription?.isInFreePlan;
      const isUpgradingPlan = isSubscriptionCheckoutRoute(router.pathname);
      const isUpgradeSuccess = isUpgradingPlan && subscription?.plan === router.query.planId;

      // If the user is in a free plan and is upgrading, and there are stopped imports,
      // we want to show the progress status
      if (isInFreePlan && isUpgradingPlan && counts.stoppedCount > 0) {
        return <FileStatusIcon status={FileStatus.preparing} />;
      }

      // If the upgrade is successful, we want to show the completed status
      if (!isInFreePlan && isUpgradeSuccess && counts.stoppedCount > 0) {
        return <FileStatusIcon status={FileStatus.completed} />;
      }

      return <FileStatusIcon status={status} />;
    },
    [subscription?.isInFreePlan, router.pathname, router.query.planId, subscription?.plan],
  );

  const showResumeAllButton = useMemo(() => {
    return hasStoppedImports && !subscription?.isInFreePlan && subscription?.pricingModel !== 'credit';
  }, [hasStoppedImports, subscription?.isInFreePlan, subscription?.pricingModel]);

  return isImportPaneVisible ? (
    <div className="mt-3">
      <FileStatusTrackingPanel
        getStatusTitle={getImportHeaderTitle}
        getSummaryTrackingAvatar={getImportSummaryTrackingAvatar}
        testId="IMPORTER_PANE"
        isExpanded
        disableCancel
        onDismiss={closePane}
        onCancelItem={onCancelImport}
        onResumeItem={onResumeImport}
        onResumeAll={showResumeAllButton ? handleResumeAll : undefined}
        items={importItems}
        getSummary={getImportSummary}
      />
    </div>
  ) : null;
});

ImportManager.displayName = 'ImportManager';
